import React, { ReactNode, useContext } from 'react';
import { AuthContextProps } from 'react-oidc-context';
import { themes } from '../components/layout/theme';
import { Credential, CredentialExchangeRecord, CredentialProofRequest } from '../types/credentials';

//================================================
// Contexts
//================================================

export type ContextProviderProps = {
  children: ReactNode;
};

//================================================
// Auth Context
//================================================
export interface IAuthState {
  /**
   * The logged in user's Keycloak information.
   *
   * @type {AuthContextProps}
   * @memberof IAuthState
   */
  auth: AuthContextProps;
}

export const AuthStateContext = React.createContext<IAuthState | undefined>(undefined);

//================================================
// Config Context
//================================================

export interface IConfig {
  //API
  LOG_LEVEL: string;
  API_HOST: string;
  //Keycloak
  KEYCLOAK_URL: string;
  KEYCLOAK_REALM: string;
  KEYCLOAK_CLIENT_ID: string;
}

export interface IConfigResponse {
  LOG_LEVEL: string;
  API_HOST: string;
  KEYCLOAK_URL: string;
  KEYCLOAK_REALM: string;
  KEYCLOAK_CLIENT_ID: string;
}

export const ConfigContext = React.createContext<IConfig | undefined>(undefined);

/**
 * Hook to access the ConfigContext
 *
 * @return {*}  {IConfig}
 */
export const useConfigContext = (): IConfig => {
  const context = useContext(ConfigContext);
  if (!context) {
    throw new Error('useConfigContext must be used within a ConfigProvider');
  }
  return context;
};

//================================================
// Credential Context
//================================================

export interface CredentialContextType {
  credentials: Credential[];
  offers: CredentialExchangeRecord[];
  proofs: CredentialProofRequest[];
  loading: boolean;
  fetchCredentials: () => void;
  fetchOffers: () => void;
  fetchProofs: () => void;
  fetchAllItems: () => void;
  addCredential: (credential: Credential) => void;
  removeOffer: (credential: CredentialExchangeRecord) => number;
  removeProof: (request: CredentialProofRequest) => number;
  removeCredential: (credential: Credential) => number;
}

export const CredentialContext = React.createContext<CredentialContextType>({
  credentials: [],
  offers: [],
  proofs: [],
  loading: false,
  fetchCredentials: () => {},
  fetchOffers: () => {},
  fetchProofs: () => {},
  fetchAllItems: () => {},
  addCredential: () => {},
  removeOffer: () => 0,
  removeProof: () => 0,
  removeCredential: () => 0
});

/**
 * Hook to access the CredentialContext
 *
 * @return {*}  {CredentialContextType}
 */
export const useCredentialData = (): CredentialContextType => {
  const context = useContext(CredentialContext);
  if (!context) {
    throw new Error('useCredentialData must be used within a ContextProvider');
  }
  return context;
};

//================================================
// Toast Context
//================================================

export interface ToastContextType {
  showToast: (message: ReactNode, timeout: number, success?: boolean) => void;
}

export const ToastContext = React.createContext<ToastContextType | undefined>(undefined);

/**
 * Hook to access the ToastContext
 *
 * @return {*}  {ToastContextType}
 */
export const useToast = (): ToastContextType => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

//================================================
// Theme Context
//================================================

export interface IThemeContext {
  theme: {
    primary: string;
    secondary: string;
    background: string;
    layoutBackground: string;
    textPrimary: string;
    textSecondary: string;
  };
}

export const ThemeContext = React.createContext({
  theme: themes.light
});

/**
 * Hook to access the ThemeContext
 *
 * @return {*}  {IConfig}
 */
export const useThemeContext = (): IThemeContext => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useThemeContext must be used within a ConfigProvider');
  }
  return context;
};

//================================================
// WebSocket Context
//================================================

export interface WebSocketContextType {
  sendMessage: (message: string) => void;
  lastMessage: MessageEvent | null;
  connectionStatus: string;
}

export const WebSocketContext = React.createContext<WebSocketContextType | undefined>(undefined);

/**
 * @description useWebSocketContext
 *
 * @return {*}  {WebSocketContextType}
 */
export const useWebSocketContext = (): WebSocketContextType => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocketContext must be used within a WebSocketProvider');
  }
  return context;
};
